import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2, HelpCircle, XCircle, Loader2, Send, MailCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOTE_META = {
  looks_good: { label: 'Looks good',     icon: CheckCircle2, className: 'border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100', activeClass: 'border-emerald-500 bg-emerald-600 text-white' },
  question:   { label: 'I have a question', icon: HelpCircle, className: 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100', activeClass: 'border-amber-500 bg-amber-600 text-white' },
  drop:       { label: 'Drop from scope', icon: XCircle,      className: 'border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100',       activeClass: 'border-rose-500 bg-rose-600 text-white' },
};

export default function StakeholderFeedbackPage() {
  const { sendId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null); // { estimate_name, components, recipient_name, ... }
  const [votes, setVotes] = useState({});   // { [componentName]: 'looks_good' | 'question' | 'drop' }
  const [notes, setNotes] = useState({});   // { [componentName]: string }
  const [overallNote, setOverallNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/scope-pack/feedback/public/${sendId}`);
      setForm(res.data);
      // Pre-fill from existing submission (if stakeholder already submitted).
      const existing = res.data?.existing?.[0];
      if (existing) {
        const v = {}; const n = {};
        for (const it of existing.items || []) {
          v[it.component_name] = it.vote;
          if (it.note) n[it.component_name] = it.note;
        }
        setVotes(v); setNotes(n);
        setOverallNote(existing.overall_note || '');
      }
    } catch (e) {
      setError(e?.response?.status === 404 ? 'This feedback link is invalid or has expired.' : 'Could not load feedback form.');
    } finally { setLoading(false); }
  }, [sendId]);

  useEffect(() => { load(); }, [load]);

  const setVote = (name, v) => setVotes(prev => ({ ...prev, [name]: prev[name] === v ? undefined : v }));
  const setNote = (name, t) => setNotes(prev => ({ ...prev, [name]: t }));

  const handleSubmit = async () => {
    const items = (form?.components || [])
      .filter(c => votes[c.name])
      .map(c => ({ component_name: c.name, vote: votes[c.name], note: (notes[c.name] || '').trim() }));
    if (items.length === 0 && !overallNote.trim()) {
      toast.error('Please mark at least one component or add an overall note.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/scope-pack/feedback/public/${sendId}`,
        { items, overall_note: overallNote.trim() });
      setThankYou(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full" data-testid="feedback-error-card">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
            <p className="text-slate-700 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (thankYou) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full" data-testid="feedback-thanks-card">
          <CardContent className="pt-8 pb-8 text-center">
            <MailCheck className="w-10 h-10 mx-auto text-[#4ABFCF] mb-3" />
            <h2 className="text-lg font-bold text-[#0F2040]">Thanks for the feedback!</h2>
            <p className="text-xs text-slate-500 mt-2">
              {form?.consultant_name || 'Your Seertech team'} will follow up shortly. You can close this tab.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="feedback-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-[#0F2040] text-white rounded-t-lg px-6 py-5">
          <div className="text-[11px] text-[#4ABFCF] uppercase tracking-wide font-bold">Seertech Solutions</div>
          <h1 className="text-lg font-bold mt-0.5">Scope Pack — Quick Feedback</h1>
          <p className="text-[11px] text-slate-300 mt-1">
            {form?.estimate_name || 'Your scope review'} &middot; for {form?.recipient_name || form?.recipient_email}
          </p>
        </div>
        <Card className="rounded-t-none border-t-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">
              Mark each component below
            </CardTitle>
            <p className="text-[11px] text-slate-500">
              Skip any you don&apos;t have an opinion on. Notes are optional — helpful where you have a question.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {(form?.components || []).length === 0 && (
              <p className="text-xs text-slate-400 italic">No components were included in this Scope Pack.</p>
            )}
            {(form?.components || []).map(c => {
              const active = votes[c.name];
              return (
                <div key={c.name} className="border border-slate-200 rounded-md p-3 space-y-2"
                  data-testid={`feedback-component-${c.name.replace(/\s/g, '-')}`}>
                  <div className="font-medium text-sm text-[#0F2040]">{c.name}</div>
                  <div className="flex flex-wrap gap-2">
                    {['looks_good', 'question', 'drop'].map(v => {
                      const meta = VOTE_META[v];
                      const Icon = meta.icon;
                      const isActive = active === v;
                      return (
                        <button key={v} type="button" onClick={() => setVote(c.name, v)}
                          className={`px-3 py-1.5 text-xs rounded-md border transition-all flex items-center gap-1.5 ${isActive ? meta.activeClass : meta.className}`}
                          data-testid={`vote-${v}-${c.name.replace(/\s/g, '-')}`}>
                          <Icon className="w-3.5 h-3.5" />
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                  {active && (
                    <Textarea value={notes[c.name] || ''}
                      onChange={e => setNote(c.name, e.target.value)}
                      rows={2} maxLength={500}
                      placeholder={active === 'looks_good' ? 'Optional comment...' : active === 'question' ? 'What would you like to clarify?' : 'What concerns you about this component?'}
                      className="text-xs"
                      data-testid={`note-${c.name.replace(/\s/g, '-')}`} />
                  )}
                </div>
              );
            })}

            <div className="pt-2">
              <div className="text-xs font-medium text-[#0F2040] mb-1">Anything else for the team?</div>
              <Textarea value={overallNote} onChange={e => setOverallNote(e.target.value)} rows={3} maxLength={2000}
                placeholder="Optional — budget constraints, timeline, stakeholders to involve, etc."
                className="text-xs" data-testid="feedback-overall-note" />
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <div className="text-[10px] text-slate-400">
                Submitting as {form?.recipient_email}
                {form?.existing?.length ? <Badge variant="secondary" className="ml-2 text-[9px]">Re-submitting an update</Badge> : null}
              </div>
              <Button onClick={handleSubmit} size="sm" disabled={submitting}
                className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
                data-testid="feedback-submit-btn">
                {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send feedback
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-slate-400 mt-4">
          Secure link · only the person who received the Scope Pack email should use this form.
        </p>
      </div>
    </div>
  );
}
